import React, { useCallback, useEffect, useRef } from 'react';

/* React redux */
import { useDispatch, useSelector } from 'react-redux';

/* Material UI */
import {
  Grid,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Button,
  useMediaQuery,
  FormHelperText,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';

/* Project */
import useForm from 'hooks/useForm';
import { formatRutNew, validatePatent, validateRut } from 'utils/functions';
import { getAnnulmentStateFromApi, setView } from 'content/app.actions';
import ErrorDialog from 'content/features/dialogs/ErrorDialog';
import LoaderDialog from 'content/features/loaders/LoaderDialog';
import { darkerGreyOnForms } from 'utils/helper';
import { closeNoRecordsError, controlsChanged, getAppointments } from '../welcomeScreens.actions';
import homeStyles from '../home.styles';
import { LoadingButton } from '@mui/lab';

function AnnulmentWelcome() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const controls = useSelector((state) => state.welcomeScreen.controls);
  const view = useSelector((state) => state.app.view);
  const loading = useSelector((state) => state.welcomeScreen.loading);
  const error = useSelector((state) => state.welcomeScreen.error);
  const errorType = useSelector((state) => state.welcomeScreen.errorType);

  useEffect(() => {
    dispatch(setView({ value: 'annulment' }));
    dispatch(getAnnulmentStateFromApi());
  }, []);
  const radioRef = useRef(null);

  const onChangeText = useCallback(
    (id, value) => {
      let newValue = value;
      if (radioRef.current === 'rut' && id === 'annulmentSearchValue') {
        newValue = formatRutNew(value);
      }
      dispatch(controlsChanged({ name: id, value: newValue }));
    },
    [radioRef],
  );

  const { onChange, onSubmit, errors, validate } = useForm(controls, null, {
    onChange: onChangeText,
    validations: {
      annulmentParameter: [
        {
          check: (value) => value !== '' && value.toString().trim().length > 0,
          message: 'Debe seleccionar una opción ',
        },
      ],
      annulmentSearchValue: [
        {
          check: (value) => value.toString().trim().length > 0,
          message: 'Debe ingresar un valor',
        },
        {
          check: (value) => (radioRef.current === 'rut' ? validateRut(value) : true),
          message: 'RUT inválido',
        },
        {
          check: (value) => (radioRef.current === 'patent' ? validatePatent(value) : true),
          message: 'Patente inválida',
        },
      ],
    },
  });

  const onChangeRadio = useCallback(
    ({ target }) => {
      radioRef.current = target.value;
      const event = { type: 'click', target: { name: 'annulmentParameter', value: target.value } };
      onChange(event);
      if (controls.annulmentParameter !== '') {
        validate('annulmentSearchValue', controls.annulmentSearchValue);
      }
    },
    [onChange],
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const { annulmentParameter, annulmentSearchValue } = controls;
    onSubmit(() =>
      dispatch(
        getAppointments({
          searchValue: annulmentSearchValue,
          searchParameter: annulmentParameter,
          view,
        }),
      ),
    );
  };

  const limitRutField = radioRef.current === 'rut' ? { maxLength: 10 } : {};

  return (
    <Grid>
      <Grid>
        <Typography color="primary" variant="h5" sx={homeStyles.title}>
          Anular tu hora
        </Typography>
        <Typography color="primary">
          Selecciona el tipo de búsqueda que quieres hacer, y luego busca para ver los agendamientos
          relacionados.
        </Typography>
      </Grid>
      <form onSubmit={(e) => handleSubmit(e)}>
        <Grid
          sx={homeStyles.container}
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap">
          <Grid>
            <FormControl>
              <RadioGroup
                row={!isMobile}
                id="annulmentParameter"
                name="annulmentParameter"
                value={controls.annulmentParameter}
                onChange={onChangeRadio}>
                <FormControlLabel
                  id="annulmentParameter"
                  name="annulmentParameter"
                  value="rut"
                  control={<Radio sx={homeStyles.radio} />}
                  label={<Typography color="primary">RUT</Typography>}
                />
                <FormControlLabel
                  id="annulmentParameter"
                  name="annulmentParameter"
                  value="patent"
                  control={<Radio sx={homeStyles.radio} />}
                  label={<Typography color="primary">Patente</Typography>}
                />
                <FormControlLabel
                  id="annulmentParameter"
                  name="annulmentParameter"
                  value="idTicket"
                  control={<Radio sx={homeStyles.radio} />}
                  label={<Typography color="primary">ID Ticket</Typography>}
                />
              </RadioGroup>
            </FormControl>
            <FormHelperText error={!!errors.annulmentParameter[0]}>
              {errors.annulmentParameter[0]}
            </FormHelperText>
          </Grid>

          <Grid item xs={12} sm={12} md={6} display="flex" gap={1} sx={homeStyles.searchContainer}>
            <TextField
              color="primary"
              label="Ingresar RUT, patente o ID Ticket"
              InputLabelProps={{
                sx: { ...darkerGreyOnForms },
              }}
              variant="outlined"
              inputProps={{ ...limitRutField }}
              sx={homeStyles.field}
              id="annulmentSearchValue"
              name="annulmentSearchValue"
              key="annulmentSearchValue"
              value={controls.annulmentSearchValue}
              onChange={onChange}
              size="small"
              error={!!errors.annulmentSearchValue[0]}
              helperText={errors.annulmentSearchValue[0]}
            />
            <LoadingButton loading={loading} type="submit" color="lightBlue" variant="contained">
              <SearchIcon sx={{ marginRight: '0.5rem', marginLeft: '0.5rem', color: 'white' }} />
            </LoadingButton>
          </Grid>
        </Grid>
      </form>
      <ErrorDialog
        open={error}
        view="annulment"
        errorType={errorType}
        handleClose={() => dispatch(closeNoRecordsError())}
      />
      <LoaderDialog open={loading} title="Buscando agendamientos" />
    </Grid>
  );
}

export default AnnulmentWelcome;
